"use client";

import Image from "next/image";
import { MouseEventHandler, useEffect, useState } from "react";

import { getAssetFileNameFromUrl, isPDF } from "@/apiUtils/commonUtils";
import CheckedIcon from "@/assets/icons/checked.svg?react";
import XIcon from "@/assets/icons/close.svg?react";
import IconNoImage from "@/assets/images/no_image.svg";
import { TranslationData } from "@/common/lib/types";
import LazyImage from "@/components/lazyImage/LazyImage";
import ConfirmationModal from "@/components/lcp/components/ConfirmationModal";
import Skeleton from "@/components/ui/skeleton/Skeleton";
import { cn } from "@/lib/tailwind";
import { getCloudinaryURL } from "@/utils/cloudinary";

interface IProps {
  className?: string;
  hideDelete?: boolean;
  onRemove?: () => void;
  initialProgress?: number;
  translations: TranslationData;
  file: { name: string; previewURL: string };
}

const UploadedItem = ({
  file,
  onRemove,
  className,
  hideDelete,
  translations,
  initialProgress,
}: IProps) => {
  const [openModal, setOpenModal] = useState(false);
  const [fileName, setFileName] = useState(file.name);
  const [progress, setProgress] = useState(initialProgress ?? 0);

  const [, extension] = fileName.split(".");
  const formattedFileName =
    fileName.length > 20 ? `${fileName?.substring(0, 20)}...${extension}` : fileName;

  const handleOpenModal: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    setOpenModal(true);
  };

  const handleRemove = async () => {
    onRemove?.();
    setOpenModal(false);
  };

  const handleFetchFileName = async () => {
    try {
      let fName = getAssetFileNameFromUrl(file.previewURL);
      if (fName) {
        setFileName(fName);
        return;
      }

      const res = await fetch(file.previewURL);
      setFileName(getAssetFileNameFromUrl(res.url));
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (initialProgress && initialProgress >= 100) return;

    const interval = setInterval(() => {
      setProgress((prevProgress) => {
        if (prevProgress < 90) return prevProgress + 10;
        else {
          clearInterval(interval);
          return 100;
        }
      });
    }, 100);

    return () => clearInterval(interval);
  }, [initialProgress]);

  useEffect(() => {
    if (file.name) return;
    handleFetchFileName();
  }, [file]);

  return (
    <div className={cn("flex items-center gap-3 bg-tertiary-5 p-3", className)}>
      {file.previewURL.toLowerCase() === "pdf" || isPDF(file.previewURL) ? (
        <p className="text-[0.75rem]">PDF</p>
      ) : file.previewURL ? (
        <LazyImage
          width={40}
          height={40}
          alt="Preview"
          className="h-6"
          src={getCloudinaryURL(file.previewURL) ?? IconNoImage}
        />
      ) : (
        <Image src={IconNoImage} alt="No image" className="h-16 w-16" />
      )}
      <div className="flex-1">
        {!fileName ? (
          <Skeleton className="mb-1 h-1 w-full bg-gray-200" />
        ) : (
          <p className="text-sm">{formattedFileName}</p>
        )}
        {progress < 100 ? (
          <div style={{ width: `${progress}%` }} className="mt-1 h-1 rounded-full bg-[#4285F4]" />
        ) : (
          <div className="flex items-center gap-3">
            <div className="bg-#34A853] -mt-[6px] h-2 w-2 rounded-full">
              <CheckedIcon />
            </div>
            <span className="text-sm text-[#34A853]">
              {translations.lensConfigurator?.lens_config_prescription_upload_completed_label}
            </span>
          </div>
        )}
      </div>
      {progress >= 100 && !hideDelete && (
        <button onClick={handleOpenModal}>
          <XIcon width={16} height={16} className="h-3 w-3" />
        </button>
      )}
      <ConfirmationModal
        open={openModal}
        onConfirm={handleRemove}
        onCancel={() => setOpenModal(false)}
        imgURL={isPDF(file.previewURL) ? "" : file.previewURL}
        slots={{
          confirm:
            translations?.lensConfigurator
              ?.lens_config_delete_prescription_overlay_confirm_button_label || "",
          cancel:
            translations?.lensConfigurator
              ?.lens_config_delete_prescription_overlay_cancel_button_label || "",
          title:
            translations?.lensConfigurator?.lens_config_delete_prescription_overlay_title || "",
        }}
      />
    </div>
  );
};

export default UploadedItem;
